import { ChangeEvent, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useMutation } from '@apollo/client';
import Checkbox from '@monofront/design-system/components/Form/Checkbox';
import Label from '@monofront/design-system/components/Form/Label';
import Question from '@monofront/design-system/visuals/Icons/Old/Question';
import { zindex } from '@monofront/theme';

import { ICreateCheckoutPaymentSessionQueryResult } from '@monofront/types';
import { CREATE_CHECKOUT_PAYMENT_SESSION } from '@monofront/business/queries';
import { useCheckoutCartState } from '@monofront/business/contexts';
import { useSubscribeInCheckout } from '@monofront/business/hooks/checkout/useSubscribeInCheckout';
import CheckoutComCardForm from '@components/PaymentForms/CheckoutComCardForm';
import Popover from '@components/Popover';
import { useCheckoutPayment } from '../contexts';
import { useAuthentication } from '@context/businessContainer';

const OptionCheckoutComCard: React.FC = () => {
  const tCard = useTranslations('Card');
  const tGlobal = useTranslations('Global');
  const { isSubscriptionAvailable } = useSubscribeInCheckout(useAuthentication);
  const { updateNewCheckoutComCard, selectedNewCheckoutComCard } =
    useCheckoutPayment();
  const { checkoutCart } = useCheckoutCartState();

  const [createCheckoutPaymentSession] =
    useMutation<ICreateCheckoutPaymentSessionQueryResult>(
      CREATE_CHECKOUT_PAYMENT_SESSION,
    );

  useEffect(() => {
    if (!checkoutCart?.id) return;
    createCheckoutPaymentSession({
      variables: { tokenValue: checkoutCart.id },
    });
  }, [checkoutCart?.id, createCheckoutPaymentSession]);

  if (!selectedNewCheckoutComCard) return null;

  return (
    <CheckoutComCardForm
      onComplete={(isValid) => {
        updateNewCheckoutComCard({
          ...selectedNewCheckoutComCard,
          isReady: isValid,
        });
      }}
      fullWidth
    >
      {!isSubscriptionAvailable && (
        <div className="tw-mb-4 tw-mt-2 tw-flex">
          <Checkbox
            name={tCard('saveCard')}
            required={false}
            id="save-card-checkbox"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              updateNewCheckoutComCard({
                ...selectedNewCheckoutComCard,
                shouldSaveCardForFutureUse: e.target.checked,
              });
            }}
            dataTestId={`NewCardForm-Checkbox-save-card`}
          />
          <div className="tw-ml-3 tw-flex tw-items-center">
            <Label id="save-card-checkbox">{tCard('saveCardLabel')}</Label>
            <Popover
              trigger="click"
              content={<div>{tCard('saveCardPopOver')}</div>}
              zIndex={zindex.popover}
            >
              <button
                type="button"
                className="tw-lf-button-clear tw-ml-2"
                aria-label={tGlobal('moreInformation')}
                data-testid="NewCardForm-button-more-information"
              >
                <Question width={14} height={14} />
              </button>
            </Popover>
          </div>
        </div>
      )}
    </CheckoutComCardForm>
  );
};

export default OptionCheckoutComCard;
